// Pure stats for the label view (#770). WhatsApp Web draws only chats that have
// a last-activity `t` and are not archived, so a label whose members have no
// conversation shows an empty list while the badge (a DB row count) stays high.
// These stats let us explain that state. Kept React/store-free so it is unit-testable.

export type LabelViewChatRow = {
    chatId: string
    t?: number | null
    archived?: boolean
}

export type LabelViewStats = {
    /** Distinct label members. */
    total: number
    /** Members WhatsApp will list: has `t` and not archived. */
    visible: number
    /** Archived members (hidden by WhatsApp's main list). */
    archived: number
    /** Not archived but no `t`, or absent from the WhatsApp store. */
    noConversation: number
}

export type LabelEmptyStateKind = 'none' | 'noConversation' | 'allArchived'

type MemberClass = 'visible' | 'archived' | 'noConversation'

function classifyMembers(
    memberChatIds: readonly string[],
    chats: readonly LabelViewChatRow[],
): Array<{ id: string; kind: MemberClass }> {
    const byId = new Map<string, LabelViewChatRow>()
    for (const chat of chats) byId.set(chat.chatId, chat)
    return [...new Set(memberChatIds)].map((id) => {
        const chat = byId.get(id)
        if (chat?.archived === true) return { id, kind: 'archived' }
        if (typeof chat?.t === 'number' && chat.t > 0) return { id, kind: 'visible' }
        return { id, kind: 'noConversation' }
    })
}

export function computeLabelViewStats(
    memberChatIds: readonly string[],
    chats: readonly LabelViewChatRow[],
): LabelViewStats {
    const stats: LabelViewStats = { total: 0, visible: 0, archived: 0, noConversation: 0 }
    for (const { kind } of classifyMembers(memberChatIds, chats)) {
        stats.total += 1
        stats[kind] += 1
    }
    return stats
}

/** Members WhatsApp will not list because they have no conversation yet (archived ones excluded). */
export function noConversationMemberIds(
    memberChatIds: readonly string[],
    chats: readonly LabelViewChatRow[],
): string[] {
    return classifyMembers(memberChatIds, chats).filter(m => m.kind === 'noConversation').map(m => m.id)
}

export function labelEmptyStateKind(stats: LabelViewStats): LabelEmptyStateKind {
    if (stats.total === 0 || stats.visible > 0) return 'none'
    return stats.noConversation > 0 ? 'noConversation' : 'allArchived'
}

export type LabelViewSnapshot = {
    stats: LabelViewStats
    noConversationIds: string[]
}

export type LabelBadge = {
    text: string
    /** Set only when the badge shows visible/total, for the tooltip. */
    visible: number | null
    total: number | null
}

const MAX_BADGE = 99999

/** Honest badge: the DB count overstates what WhatsApp lists, so show visible/total once that is known. */
export function labelBadge(dbCount: number, stats: LabelViewStats | null): LabelBadge {
    if (stats && stats.total > 0 && stats.visible < stats.total) {
        return { text: `${stats.visible}/${stats.total}`, visible: stats.visible, total: stats.total }
    }
    return { text: dbCount > MAX_BADGE ? `${MAX_BADGE}+` : String(dbCount), visible: null, total: null }
}

export type LabelEmptyStateCopy = {
    titleKey: 'labels.emptyView.noConversation' | 'labels.emptyView.allArchived'
    titleCount: number
    archivedCount: number
}

/** Which message to show and with which counts; null when the label view needs no explanation. */
export function labelEmptyStateCopy(stats: LabelViewStats): LabelEmptyStateCopy | null {
    const kind = labelEmptyStateKind(stats)
    if (kind === 'none') return null
    return kind === 'noConversation'
        ? { titleKey: 'labels.emptyView.noConversation', titleCount: stats.noConversation, archivedCount: stats.archived }
        : { titleKey: 'labels.emptyView.allArchived', titleCount: stats.archived, archivedCount: stats.archived }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

const isCount = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0

/** Validates the page-context reply; anything that is not a well-formed snapshot (void, error envelope) is undefined. */
export function parseLabelViewSnapshot(value: unknown): LabelViewSnapshot | undefined {
    if (!isRecord(value) || !isRecord(value.stats) || !Array.isArray(value.noConversationIds)) return undefined
    const { total, visible, archived, noConversation } = value.stats
    if (!isCount(total) || !isCount(visible) || !isCount(archived) || !isCount(noConversation)) return undefined
    const ids = value.noConversationIds
    if (!ids.every((id): id is string => typeof id === 'string')) return undefined
    return { stats: { total, visible, archived, noConversation }, noConversationIds: ids }
}
